import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Box, Card, CardContent, Divider, Grid } from "@mui/material";

import ModalLoading from "../../../../components/Formularios/Modal/ModalLoading";
import ModalDetalhe from "../modalDetalheCodigoSistema";
import { showConfirmationDialog } from "../../../../components/Formularios/Modal/ModalExcluir";
import { useForm } from "react-hook-form";
import { endpointPrincipal } from "../../../../api/endPoints/endpointPrincipal/endpointPrincipal";
import FormTitulo from "../../../../components/Formularios/Container/FormTitulo";
import FormActions from "../../../../components/Formularios/Buttons/FormActions";
import CustomTextField from "../../../../components/Formularios/Inputs/CustomTextField";
import SwitchAtivo from "../../../../components/Formularios/Inputs/SwitchAtivo";
import ActionToolbar from "../../../../components/ActionToolbar/ActionToolbar";
import CustomTable, { ColunaTabela } from "../../../../components/Formularios/Table/CustomTable";
import usePaginacaoLocal from "../../../../hooks/usePaginacaoLocal";
import { verificaSeAtivoERetornaBadge } from "../../../../utils/verificaSeAtivoERetornaBadge/verificaSeAtivoERetornaBadge";

interface CodSistemaInterface {
  id?: string;
  tabela: string;
  descricao: string;
  situacao: number;
  itens?: any[];
}

interface CodSistemaDetalheInterface {
  codigo: string;
  descricao: string;
  defusr01?: string;
  defusr02?: string;
  defusr03?: string;
  defusr04?: string;
  defusr05?: string;
  defusr06?: string;
  situacao: number;
}

interface FormPerfilProps {
  idcodigo?: string;
  acao?: string;
}

const colunas: ColunaTabela<CodSistemaDetalheInterface>[] = [
  { key: "codigo", label: "Código" },
  { key: "descricao", label: "Descrição" },
  { key: "defusr01", label: "Def. Usuário 1" },
  { key: "defusr02", label: "Def. Usuário 2" },
  { key: "defusr03", label: "Def. Usuário 3" },
  { key: "defusr04", label: "Def. Usuário 4" },
  { key: "defusr05", label: "Def. Usuário 5" },
  { key: "defusr06", label: "Def. Usuário 6" },
  {
    key: "situacao",
    label: "Situação",
    render: (situacao) => verificaSeAtivoERetornaBadge(String(situacao)),
    align: "center",
  },
];

const FormCodSistema = ({ idcodigo, acao }: FormPerfilProps) => {
  const { register, handleSubmit, setValue, watch, control } = useForm<CodSistemaInterface>({
    defaultValues: { situacao: 1 },
  });
  const navigate = useNavigate();

  // Define se está no modo de visualização
  const isVisualizando = acao === 'Visualizar';

  const [loading, setLoading] = useState<boolean>(false);
  const [dados, setDados] = useState<CodSistemaDetalheInterface[]>([]);
  const [showModalDetalhe, setShowModalDetalhe] = useState<boolean>(false);
  const [dadosItens, setDadosItens] = useState({
    codigo: "",
    descricao: "",
    defUsuario01: "",
    defUsuario02: "",
    defUsuario03: "",
    defUsuario04: "",
    defUsuario05: "",
    defUsuario06: "",
    ativo: true,
    acao: "",
  });

  const paginacaoItens = usePaginacaoLocal(dados, 5);

  const carregarDados = async (tabela: string) => {
    setLoading(true);
    try {
      const { data } = await endpointPrincipal.get(`/codigosistema/${tabela}`);
      setValue("id", data.id);
      setValue("tabela", data.tabela);
      setValue("descricao", data.descricao);
      setValue("situacao", data.situacao === 1 ? 1 : 0);
      setDados(data.detalhes || []);
    } catch (erro: any) {
      const msg = erro.response?.data.message || erro.message;
      toast.error(`Erro ao carregar dados. Motivo: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (idcodigo && idcodigo !== "novo") {
      carregarDados(idcodigo);
    }
  }, [idcodigo]);

  //#region REALIZA REQUISIÇÃO PARA ATUALIZAR OS DADOS
  const atualizarDados = async (tabela: string, dto: any) => {
    try {
      const { data } = await endpointPrincipal.put(`/codigosistema/${tabela}`, dto);
      toast.success(data.message);
      setTimeout(() => navigate(-1), 2000);
    } catch (erro: any) {
      const msg = erro.response?.data.message || erro.message;
      toast.error(`Erro ao atualizar dados. Motivo: ${msg}`);
    }
  };

  //#region REALIZA REQUISIÇÃO PARA GRAVAR OS DADOS
  const gravarDados = async (dto: any) => {
    try {
      const { data } = await endpointPrincipal.post("/codigosistema", dto);
      toast.success(data.message);
      setTimeout(() => navigate(-1), 2000);
    } catch (erro: any) {
      const msg = erro.response?.data.message || erro.message;
      toast.error(`Erro ao gravar dados. Motivo: ${msg}`);
    }
  };

  const onSubmit = (formData: CodSistemaInterface) => {
    const itensComTabela = dados.map(item => ({
      ...item,
      tabela: formData.tabela.toUpperCase()
    }));

    const dto = {
      ...formData,
      tabela: formData.tabela.toUpperCase(),
      situacao: formData.situacao === 1 ? 1 : 0,
      itens: itensComTabela
    };

    if (idcodigo === "novo" || !idcodigo) {
      gravarDados(dto);
      toast.info("Inserindo novo registro...");
    } else {
      atualizarDados(formData.tabela, dto);
      toast.info("Atualizando dados do registro...");
    }
  };

  const visualizar = (dadosVisualizar: any) => {
    setDadosItens((state) => {
      return {
        ...state,
        codigo: dadosVisualizar.codigo,
        descricao: dadosVisualizar.descricao,
        defUsuario01: dadosVisualizar.defusr01,
        defUsuario02: dadosVisualizar.defusr02,
        defUsuario03: dadosVisualizar.defusr03,
        defUsuario04: dadosVisualizar.defusr04,
        defUsuario05: dadosVisualizar.defusr05,
        defUsuario06: dadosVisualizar.defusr06,
        ativo: dadosVisualizar.situacao === 1 ? true : false,
        acao: "Visualizar",
      };
    });
    setShowModalDetalhe(true);
  };

  const editar = (dadosEditar: any) => {
    setDadosItens((state) => {
      return {
        ...state,
        codigo: dadosEditar.codigo,
        descricao: dadosEditar.descricao,
        defUsuario01: dadosEditar.defusr01,
        defUsuario02: dadosEditar.defusr02,
        defUsuario03: dadosEditar.defusr03,
        defUsuario04: dadosEditar.defusr04,
        defUsuario05: dadosEditar.defusr05,
        defUsuario06: dadosEditar.defusr06,
        ativo: dadosEditar.situacao === 1 ? true : false,
        acao: "EditarItem",
      };
    });
    setShowModalDetalhe(true);
  };
  const excluirItem = async (dadosExcluir: any) => {
    const codigo = dadosExcluir.codigo;
    if (acao !== "Visualizar") {
      await showConfirmationDialog(
        "Excluir Código do Sistema",
        `Tem certeza que deseja excluir o código do sistema \n ${dadosExcluir.codigo} - ${dadosExcluir.descricao}?`,
        "question",
        "Excluir",
        "Cancelar"
      )
        .then(async (resultado) => {
          if (resultado.confirmed) {
            if (acao === "Novo") {
              const dadosNovos = dados.filter(
                (item: any) => item.codigo !== codigo
              );
              setDados(dadosNovos);
              toast.success("Código excluído com sucesso!");
            } else if (acao === "Editar") {
              try {
                const currentTabela = watch("tabela");
                const { data } = await endpointPrincipal.delete(`/codigosistema/${currentTabela}/detalhe/${codigo}`);
                toast.success(data.message);
                carregarDados(currentTabela);
              } catch (erro: any) {
                const msg = erro.response?.data.message || erro.message;
                toast.error(`Erro ao excluir registro. Motivo: ${msg}`);
              }
            }
          }
        })
        .catch((erro: any) => {
          const msg = erro.response?.data.message || erro.message;
          toast.error(`Erro ao excluir dados. Motivo: ${msg}`);
        });
    }
  };

  const handleOpenModal = () => {
    //Limpar Campos do Modal
    setDadosItens({
      codigo: "",
      descricao: "",
      defUsuario01: "",
      defUsuario02: "",
      defUsuario03: "",
      defUsuario04: "",
      defUsuario05: "",
      defUsuario06: "",
      ativo: true,
      acao: "Novo",
    });

    setShowModalDetalhe(true);
  };
  const handleCloseModal = () => {
    // Simulação: Buscar dados de um GET ou preparar valores iniciais
    setShowModalDetalhe(false);
  };

  const handleSubmitForm = async (dadosItens: any) => {
    const currentTabela = watch("tabela");
    const dadosTabela = {
      tabela: currentTabela.toUpperCase(),
      codigo: dadosItens.codigo.toUpperCase(),
      descricao: dadosItens.descricao,
      defusr01: dadosItens.defUsuario01,
      defusr02: dadosItens.defUsuario02,
      defusr03: dadosItens.defUsuario03,
      defusr04: dadosItens.defUsuario04,
      defusr05: dadosItens.defUsuario05,
      defusr06: dadosItens.defUsuario06,
      situacao: dadosItens.ativo ? 1 : 0,
    };

    try {
      if (acao === "Novo" && dadosItens.acao === "Novo") {
        const item = dados.some((item) => item.codigo === dadosItens.codigo);
        if (!item) {
          setShowModalDetalhe(false);
          setDados([...dados, dadosTabela]);
        } else {
          toast.error("Código já cadastrado na tabela!");
        }
      } else if (acao === "Editar" && dadosItens.acao === "Novo") {
        const item = dados.some((item) => item.codigo === dadosItens.codigo);
        if (!item) {
          const { data } = await endpointPrincipal.post(`/codigosistema/${currentTabela}/detalhe`, dadosTabela);
          toast.success(data.message);
          carregarDados(currentTabela);
          setShowModalDetalhe(false);
        } else {
          toast.error("Código já cadastrado na tabela!");
        }
      } else if (acao === "Editar" && dadosItens.acao === "EditarItem") {
        const { data } = await endpointPrincipal.put(`/codigosistema/${currentTabela}/detalhe/${dadosItens.codigo}`, dadosTabela);
        toast.success(data.message);
        carregarDados(currentTabela);
        setShowModalDetalhe(false);
      }
    } catch (erro: any) {
      const msg = erro.response?.data.message || erro.message;
      toast.error(`Erro ao processar dados. Motivo: ${msg}`);
    }
  };

  return (
    <>
      <Card>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <FormTitulo titulo="Códigos do Sistema" acao={acao} />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={2}>
                <CustomTextField
                  label="Cód. Tabela"
                  name="id"
                  register={register}
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <CustomTextField
                  label="Tabela"
                  name="tabela"
                  register={register}
                  rules={{ required: true }}
                  required
                  disabled={isVisualizando || acao === "Editar"}
                  inputProps={{
                    style: { textTransform: 'uppercase' }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <CustomTextField
                  label="Descrição"
                  name="descricao"
                  register={register}
                  rules={{ required: true }}
                  required
                  disabled={isVisualizando}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <SwitchAtivo name="situacao" control={control} disabled={isVisualizando} />
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <ActionToolbar
            onIncluir={handleOpenModal}
            disabledIncluir={isVisualizando}
            mostrarFiltrar={false}
          />
          <CustomTable
            columns={colunas}
            data={paginacaoItens.itensPagina}
            idField="codigo"
            onVisualizar={visualizar}
            onEditar={isVisualizando ? undefined : editar}
            onDelete={isVisualizando ? undefined : excluirItem}
            paginaAtual={paginacaoItens.paginaAtual}
            totalPaginas={paginacaoItens.totalPaginas}
            setPaginaAtual={paginacaoItens.setPaginaAtual}
            maxHeight="45vh"
          />

          <FormActions onVoltar={() => navigate(-1)} visualizar={isVisualizando} />
        </Box>
      </Card>
      <ModalLoading show={loading} label="Carregado dados do perfil ..." />

      {/* MODAL PARA INCLUIR/EDITAR ITEM NA TABELA */}

      <ModalDetalhe
        showModal={showModalDetalhe}
        onClose={handleCloseModal}
        onSubmit={handleSubmitForm}
        acao={dadosItens.acao}
        dadosCodigo={dadosItens.codigo}
        dadosDescricao={dadosItens.descricao}
        dadosDefUsuario01={dadosItens.defUsuario01}
        dadosDefUsuario02={dadosItens.defUsuario02}
        dadosDefUsuario03={dadosItens.defUsuario03}
        dadosDefUsuario04={dadosItens.defUsuario04}
        dadosDefUsuario05={dadosItens.defUsuario05}
        dadosDefUsuario06={dadosItens.defUsuario06}
        dadosAtivo={dadosItens.ativo}
      />
    </>
  );
};
export default FormCodSistema;
